import React, { useRef, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Box, Container, Typography, Button } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { useAuth } from '@/hooks/useAuth'

const GOOGLE_CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID || ''

declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize: (config: object) => void
          renderButton: (element: HTMLElement, config: object) => void
        }
      }
    }
  }
}

// ── Reusable Google button ────────────────────────────────────────────────────
let googleInitialized = false
let googleCallback: ((res: { credential: string }) => void) | null = null

const GoogleButton: React.FC<{ width?: number; id?: string }> = ({ width = 320, id = 'google-btn' }) => {
  const ref = useRef<HTMLDivElement>(null)
  const navigate = useNavigate()
  const { loginWithGoogle } = useAuth()

  useEffect(() => {
    if (!GOOGLE_CLIENT_ID) return

    googleCallback = async (res: { credential: string }) => {
      try {
        await loginWithGoogle(res.credential)
        navigate('/dashboard')
      } catch {}
    }

    const renderBtn = () => {
      if (!window.google || !ref.current) return
      if (!googleInitialized) {
        window.google.accounts.id.initialize({
          client_id: GOOGLE_CLIENT_ID,
          callback: (res: { credential: string }) => googleCallback?.(res),
          auto_select: false,
        })
        googleInitialized = true
      }
      window.google.accounts.id.renderButton(ref.current as HTMLElement, {
        type: 'standard', theme: 'outline', size: 'large',
        width, text: 'continue_with', logo_alignment: 'left',
      })
    }

    const scriptId = 'google-gis-script'
    if (document.getElementById(scriptId)) { renderBtn(); return }
    const s = document.createElement('script')
    s.id = scriptId; s.src = 'https://accounts.google.com/gsi/client'
    s.async = true; s.defer = true; s.onload = renderBtn
    document.head.appendChild(s)
  }, [])

  if (!GOOGLE_CLIENT_ID) return null
  return <Box ref={ref} id={id} />
}

// ── App screenshot preview ────────────────────────────────────────────────────
const AppPreview: React.FC = () => (
  <Box sx={{ position: 'relative', borderRadius: 3, overflow: 'hidden', height: 400, boxShadow: '0 8px 40px rgba(0,0,0,0.12)' }}>
    {/* screenshot fills container like object-cover */}
    <Box
      component="img"
      src="/images/app-preview.png"
      alt="Orelytics app preview"
      sx={{
        position: 'absolute', inset: 0,
        width: '100%', height: '100%',
        objectFit: 'cover', objectPosition: 'center top',
        transform: 'scale(1.02)',
        transformOrigin: 'center top',
        opacity: 0.9,
        filter: 'blur(0.8px)',
      }}
    />
    {/* white overlay */}
    <Box sx={{ position: 'absolute', inset: 0, bgcolor: 'rgba(255,255,255,0.2)', pointerEvents: 'none' }} />
    {/* bottom fade */}
    <Box sx={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: '30%', background: 'linear-gradient(to bottom, transparent, white)', pointerEvents: 'none' }} />
    {/* floating label */}
    <Box sx={{
      position: 'absolute', top: 16, right: 16,
      bgcolor: 'rgba(0,0,0,0.65)', color: 'white',
      px: 1.5, py: 0.5, borderRadius: 10,
      fontSize: '0.7rem', fontWeight: 600, letterSpacing: 0.5,
      backdropFilter: 'blur(2px)',
    }}>
      Real project data
    </Box>
  </Box>
)

// ── Section wrapper ───────────────────────────────────────────────────────────
const Section: React.FC<{ children: React.ReactNode; bg?: string; py?: number }> = ({ children, bg, py = 10 }) => (
  <Box sx={{ bgcolor: bg || 'background.default', py }}>
    <Container maxWidth="md">{children}</Container>
  </Box>
)

// ── Main landing ──────────────────────────────────────────────────────────────
export const LandingPage: React.FC = () => {
  const navigate = useNavigate()
  const { t } = useTranslation()

  const accessItems = t('landing.access.items', { returnObjects: true }) as string[]
  const featureItems = t('landing.features.items', { returnObjects: true }) as string[]
  const versionItems = t('landing.version.items', { returnObjects: true }) as string[]

  return (
    <Box sx={{ minHeight: '100vh' }}>

      {/* ── HERO ── */}
      <Box sx={{
        position: 'relative', minHeight: '100vh', display: 'flex', alignItems: 'center',
        backgroundImage: 'url(/images/escondida-open-pit.jpg)',
        backgroundSize: 'cover', backgroundPosition: 'center',
        '&::before': { content: '""', position: 'absolute', inset: 0, bgcolor: 'rgba(0,0,0,0.62)', zIndex: 0 },
      }}>
        <Container maxWidth="sm" sx={{ position: 'relative', zIndex: 1, textAlign: 'center', color: 'white' }}>
          <Typography variant="h2" sx={{ fontWeight: 700, letterSpacing: -1, mb: 1 }}>
            orelytics
          </Typography>
          <Typography variant="h5" sx={{ fontWeight: 300, mb: 2, opacity: 0.9, fontStyle: 'italic' }}>
            {t('landing.hero.tagline')}
          </Typography>
          <Typography variant="body1" sx={{ mb: 1, opacity: 0.85, maxWidth: 420, mx: 'auto' }}>
            {t('landing.hero.subtitle')}
          </Typography>
          <Typography variant="body2" sx={{ mb: 5, opacity: 0.65 }}>
            {t('landing.hero.supporting')}
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5 }}>
            <GoogleButton width={300} id="google-btn-hero" />
            <Button variant="outlined" size="large" onClick={() => navigate('/login')}
              sx={{ color: 'white', borderColor: 'rgba(255,255,255,0.5)', width: 300, '&:hover': { borderColor: 'white', bgcolor: 'rgba(255,255,255,0.08)' } }}>
              {t('landing.hero.emailCta')}
            </Button>
            <Typography variant="caption" sx={{ opacity: 0.7 }}>
              {t('landing.hero.microcopyText')}
              <Box component="a" href="/register" sx={{ color: 'white', textDecoration: 'underline', cursor: 'pointer' }}>
                {t('landing.hero.microcopyLink')}
              </Box>
              {t('landing.hero.microcopyTextAfter')}
            </Typography>
          </Box>
        </Container>
      </Box>

      {/* ── PROBLEM ── */}
      <Section bg="background.paper">
        <Typography variant="body1" sx={{ textAlign: 'center', maxWidth: 560, mx: 'auto', color: 'text.secondary', lineHeight: 1.8 }}>
          {t('landing.problem.text')}
        </Typography>
      </Section>

      {/* ── ACCESS ── */}
      <Section>
        <Typography variant="h4" sx={{ fontWeight: 700, mb: 1, textAlign: 'center' }}>{t('landing.access.title')}</Typography>
        <Box component="ul" sx={{ listStyle: 'none', p: 0, m: '0 auto', maxWidth: 320, textAlign: 'center' }}>
          {accessItems.map(item => (
            <Box component="li" key={item} sx={{ py: 0.5, color: 'text.secondary' }}>
              <Box component="span" sx={{ color: 'success.main', mr: 1 }}>✓</Box>{item}
            </Box>
          ))}
        </Box>
      </Section>

      {/* ── PRODUCT PREVIEW ── */}
      <Section bg="background.paper" py={8}>
        <Typography variant="h4" sx={{ fontWeight: 700, mb: 1, textAlign: 'center' }}>{t('landing.preview.title')}</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', mb: 4 }}>
          {t('landing.preview.subtitle')}
        </Typography>
        <AppPreview />
      </Section>

      {/* ── FEATURES ── */}
      <Section>
        <Typography variant="h4" sx={{ fontWeight: 700, mb: 1, textAlign: 'center' }}>{t('landing.features.title')}</Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, maxWidth: 400, mx: 'auto', mt: 3 }}>
          {featureItems.map(f => (
            <Box key={f} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: 'primary.main', flexShrink: 0 }} />
              <Typography variant="body1">{f}</Typography>
            </Box>
          ))}
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', mt: 3 }}>
          {t('landing.features.supporting')}
        </Typography>
      </Section>

      {/* ── COLLABORATION ── */}
      <Section bg="background.paper">
        <Typography variant="h4" sx={{ fontWeight: 700, mb: 2, textAlign: 'center' }}>{t('landing.collaboration.title')}</Typography>
        <Typography variant="body1" color="text.secondary" sx={{ textAlign: 'center', maxWidth: 480, mx: 'auto' }}>
          {t('landing.collaboration.text')}
        </Typography>
      </Section>

      {/* ── VERSION NOTE ── */}
      <Section>
        <Typography variant="h5" sx={{ fontWeight: 700, mb: 2, textAlign: 'center' }}>{t('landing.version.title')}</Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, maxWidth: 360, mx: 'auto' }}>
          {versionItems.map(item => (
            <Box key={item} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: 'text.disabled', flexShrink: 0 }} />
              <Typography variant="body2" color="text.secondary">{item}</Typography>
            </Box>
          ))}
        </Box>
        <Typography variant="caption" color="text.disabled" sx={{ display: 'block', textAlign: 'center', mt: 2 }}>
          {t('landing.version.note')}
        </Typography>
      </Section>

      {/* ── FINAL CTA ── */}
      <Box sx={{ bgcolor: 'primary.main', py: 10 }}>
        <Container maxWidth="sm" sx={{ textAlign: 'center' }}>
          <Typography variant="h4" sx={{ fontWeight: 700, color: 'white', mb: 4 }}>{t('landing.finalCta.title')}</Typography>
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
            <GoogleButton width={300} id="google-btn-cta" />
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2 }}>
            <Button variant="text" onClick={() => navigate('/login')}
              sx={{ color: 'rgba(255,255,255,0.7)', textDecoration: 'underline', fontSize: '0.85rem', '&:hover': { color: 'white' } }}>
              {t('landing.hero.emailCta')}
            </Button>
            <Button variant="text" onClick={() => navigate('/register')}
              sx={{ color: 'rgba(255,255,255,0.7)', textDecoration: 'underline', fontSize: '0.85rem', '&:hover': { color: 'white' } }}>
              {t('landing.finalCta.register')}
            </Button>
          </Box>
        </Container>
      </Box>

    </Box>
  )
}
